import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import { useAuth } from '../contexts/AuthContext';

/**
 * TrialBanner — shown at the top of dashboards when a company is on a free trial or has a payment issue.
 *
 * Props:
 *   company  — the company record (needs trial_end_date, subscription_status)
 *   isDemoAdmin — hide banner for demo accounts
 */
const TrialBanner = ({ company, isDemoAdmin }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Billing UI is admin-only. Site managers, workers, and clients should not see
  // trial/payment banners even if this component is embedded on their dashboards.
  const isAdmin = userProfile?.role === 'admin';

  const bannerInfo = useMemo(() => {
    if (!company) return null;

    const status = company?.subscription_status;

    // past_due / unpaid are handled by SubscriptionReadOnlyBanner.
    // This component only owns the trialing banner now.
    if (status !== 'trialing') return null;

    const trialEnd = company?.trial_end_date;
    if (!trialEnd) return null;

    const now = new Date();
    const endDate = new Date(trialEnd);
    const diffMs = endDate - now;
    const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    // Trial has ended — handled by lock screen
    if (daysRemaining <= 0) return null;

    return { type: 'trialing', daysRemaining, endDate };
  }, [company]);

  if (!isAdmin || isDemoAdmin || !bannerInfo) return null;

  // Trial banner
  const { daysRemaining } = bannerInfo;
  const isFinalDay = daysRemaining === 1;
  const isUrgent = daysRemaining <= 3;
  const isWarning = daysRemaining <= 7;

  const bannerClass = isUrgent
    ? 'bg-red-50 border-red-200 text-red-800'
    : isWarning
    ? 'bg-amber-50 border-amber-200 text-amber-800' :'bg-blue-50 border-blue-200 text-blue-800';

  const iconName = isUrgent ? 'AlertCircle' : 'Clock';
  const iconColor = isUrgent ? '#dc2626' : isWarning ? '#d97706' : '#2563eb';

  // Final-day variant has a stronger call to action — without a payment method
  // the subscription pauses tomorrow (Stripe trial_settings.end_behavior=pause).
  const ctaLabel = isFinalDay ? 'Add Payment Now →' : 'Manage Subscription →';

  return (
    <div className={`border rounded-lg px-4 py-3 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${bannerClass}`}>
      <div className="flex items-center gap-3">
        <Icon name={iconName} size={18} color={iconColor} className="flex-shrink-0" />
        <div>
          {isFinalDay ? (
            <>
              <p className="text-sm font-medium">
                <span className="font-bold">Trial ends tomorrow</span> — add a payment method now to keep working without interruption.
              </p>
              <p className="text-xs mt-0.5 opacity-80">
                If no card is on file, your account pauses tomorrow and you'll be read-only until you add one.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">
                You're on your 30-day free trial —{' '}
                <span className="font-bold">
                  {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining.
                </span>
              </p>
              <p className="text-xs mt-0.5 opacity-80">
                You won't be charged if you cancel before your trial ends.
              </p>
            </>
          )}
        </div>
      </div>
      <button
        onClick={() => navigate('/subscription-management')}
        className="text-sm font-semibold underline underline-offset-2 whitespace-nowrap flex-shrink-0 hover:opacity-80 transition-opacity"
      >
        {ctaLabel}
      </button>
    </div>
  );
};

export default TrialBanner;
